import { Fragment, useState, useEffect, useContext } from "react";
import { UserContext } from "../../../../../../context/UserContext";
import { options, styles } from "../util";
import { setCreditCard } from "../util/ConfigureAccounts";
import {
  Stack,
  Typography,
  Button,
  TextField as MuiTextField,
  Autocomplete as MuiAutocomplete,
  Grid,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import { Field } from "formik";
import { FormContainer, NavButtons } from "../../../../Menu/Flow";
import api from "../../../../../../api/axios";
import employerFlowStyles from "../../styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export default function Billing(props) {
  const { mode, exitUrl, step, formik, displayOnly, employerId, stripeStatus } =
    props;
  const { billingAccount, reimbursementAccount } = props;
  const { errors, touched, values, setFieldValue, setFieldTouched, setErrors } =
    formik;
  const { user, getEmployer } = useContext(UserContext);
  const employer = getEmployer();
  const hideNext = mode === "manage";

  const showCompleteSetupLater =
    !hideNext &&
    employer?.partner?.id &&
    employer?.partner?.id === user?.partnerId;

  const [banksState, setBanksState] = useState([]);
  const [allBanks, setAllBanks] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [banksLoaded, setBanksLoaded] = useState(false);
  const [bankInputValue, setBankInputValue] = useState("");

  // Verification status of the stored bank account (shown when set)
  const [verifiedTs, setVerifiedTs] = useState(null);

  const billingMethod = values?.billingMethod?.value;
  let showBank = false;
  let showStripe = false;

  if (billingMethod === "REIMBURSEMENT") showBank = true;
  else if (billingMethod === "BILLING") showBank = true;
  else if (billingMethod === "STRIPE") showStripe = true;

  // Bank fields disabled if displayOnly OR billing method is REIMBURSEMENT
  // (REIMBURSEMENT reuses the existing reimbursement account — not editable here)
  const isBankFieldsDisabled =
    displayOnly ||
    (billingMethod === "REIMBURSEMENT" &&
      billingAccount?.id === reimbursementAccount?.id);

  // A BILLING account's stored verification no longer applies once the bank changes
  const requiresVerification = billingMethod === "BILLING";

  // ── Initialize once employer is available ─────────────────────────────────
  useEffect(() => {
    if (!employer) return;
    if (initialized) return;

    // Determine billing method
    let initialBillingMethod = null;
    if (stripeStatus === "ACTIVE") {
      initialBillingMethod = options
        .billingMethods(reimbursementAccount)
        .find((opt) => opt.value === "STRIPE");
    } else if (billingAccount?.id) {
      if (billingAccount.id === reimbursementAccount?.id) {
        initialBillingMethod = options
          .billingMethods(reimbursementAccount)
          .find((opt) => opt.value === "REIMBURSEMENT");
      } else {
        initialBillingMethod = options
          .billingMethods(reimbursementAccount)
          .find((opt) => opt.value === "BILLING");
      }
    }

    if (initialBillingMethod) {
      setFieldValue("billingMethod", initialBillingMethod, false);
    }

    // Initialize verifiedTs from existing billing account if it's a separate account
    if (billingAccount?.id && billingAccount?.id !== reimbursementAccount?.id) {
      setVerifiedTs(billingAccount?.verifiedTs || null);
    }

    setInitialized(true);
  }, [billingAccount, reimbursementAccount, stripeStatus, employer]);

  // Sync input value with form values when bank is set
  useEffect(() => {
    if (values.bank?.routingNumber) {
      setBankInputValue(values.bank.routingNumber);
    } else if (!values.bank) {
      setBankInputValue("");
    }
  }, [values.bank]);

  // ── Load banks ────────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchBanks() {
      try {
        const response = await api.get("/bank");
        const banks = response?.data?.data || [];
        setAllBanks(banks);
      } catch (error) {
        setAllBanks([]);
      } finally {
        setBanksLoaded(true);
      }
    }
    fetchBanks();
  }, []);

  // ── Match existing routing number to full bank object once allBanks loads ──
  useEffect(() => {
    if (!allBanks.length || !bankInputValue) return;
    if (values.bank?.plaidVerificationIssueTs !== undefined) return; // already resolved

    const matchedBank = allBanks.find(
      (b) => b.routingNumber === bankInputValue,
    );
    if (matchedBank) {
      setFieldValue(
        "bank",
        {
          ...matchedBank,
          displayName: `${matchedBank.routingNumber} - ${matchedBank.bankName}`,
        },
        false,
      );
    }
  }, [allBanks, bankInputValue]);

  // ── Stripe ────────────────────────────────────────────────────────────────
  async function handleStripe() {
    const newParams = new URLSearchParams(window.location.search);
    newParams.delete("stripe");
    const newUrl = newParams.toString()
      ? `${window.location.pathname}?${newParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, "", newUrl);
    const stripeMode = stripeStatus === "ACTIVE" ? "edit" : "add";
    await setCreditCard(employerId, stripeMode);
  }

  // ── Bank search ───────────────────────────────────────────────────────────
  function searchBanks(routingNumber) {
    if (!routingNumber) {
      setBanksState([]);
      return;
    }
    const foundBanks = allBanks.filter((bank) => {
      const routingMatch = bank.routingNumber?.includes(routingNumber);
      const nameMatch = bank.bankName
        ?.toLowerCase()
        .includes(routingNumber.toLowerCase());
      return routingMatch || nameMatch;
    });
    if (!foundBanks || foundBanks.length === 0) {
      setBanksState([]);
      return;
    }
    const formattedBanks = foundBanks.slice(0, 5).map((bank) => ({
      ...bank,
      displayName: `${bank?.routingNumber || ""} - ${bank?.bankName || ""}`,
    }));
    setBanksState(formattedBanks);
  }

  function setBankAccountFields(bankAccount) {
    const { routingNumber, nickname } = bankAccount;
    const bank = {
      ...bankAccount,
      displayName: `${routingNumber || "000000000"} - ${nickname || bankAccount?.bankName || ""}`,
    };
    setFieldValue("bank", bank);
    setFieldValue("bankName", bankAccount?.bankName);
    setFieldValue("nickname", bankAccount?.nickname || bankAccount?.bankName);
    setFieldValue("accountType", bankAccount?.accountType || "checking");
    setFieldValue("accountNumber", bankAccount?.accountNumber || "");
    setBankInputValue(routingNumber || "");
  }

  function clearBankAccountFields() {
    setFieldValue("bank", options.nullBankOption());
    setFieldValue("bankName", "");
    setFieldValue("nickname", "");
    setFieldValue("accountType", "checking");
    setFieldValue("accountNumber", "");
    setFieldValue("bankAccountId", null);
    setBankInputValue("");
  }

  const handleBillingMethodChange = (e) => {
    const selectedValue = e.target.value;
    const selectedOption = options
      .billingMethods(reimbursementAccount)
      .find((opt) => opt.value === selectedValue);

    setFieldValue("billingMethod", selectedOption, false);
    setFieldTouched("billingMethod", false, false);

    if (selectedValue === "REIMBURSEMENT") {
      setBankAccountFields(reimbursementAccount);
      // REIMBURSEMENT account is already verified — use its verifiedTs
      setVerifiedTs(reimbursementAccount?.verifiedTs || Date.now());
    } else if (selectedValue === "BILLING") {
      if (
        billingAccount?.id &&
        billingAccount?.id !== reimbursementAccount?.id
      ) {
        setBankAccountFields(billingAccount);
        setVerifiedTs(billingAccount?.verifiedTs || null);
      } else {
        clearBankAccountFields();
        setVerifiedTs(null);
      }
    } else if (selectedValue === "STRIPE") {
      clearBankAccountFields();
      setVerifiedTs(null);
    }
  };

  function handleBankInput(routingNumber) {
    setBankInputValue(routingNumber || "");
    if (routingNumber) {
      searchBanks(routingNumber);
    } else {
      setBanksState([]);
    }
    // Routing number change invalidates verification
    if (requiresVerification) setVerifiedTs(null);
  }

  const handleBankSelect = (event, bank) => {
    if (bank?.bankName) {
      if (bank?.routingNumber === billingAccount?.routingNumber) {
        bank = { ...bank, ...billingAccount };
      } else if (bank?.routingNumber === reimbursementAccount?.routingNumber) {
        bank = { ...bank, ...reimbursementAccount };
      }
      setFieldValue("bank", bank);
      setFieldValue("bankName", bank?.bankName);
      setFieldValue("nickname", bank?.nickname || bank?.bankName);
      setFieldValue("accountType", values.accountType || "checking");
      setFieldValue("accountNumber", values.accountNumber || "");
      setBankInputValue(bank?.routingNumber || bank?.displayName || "");
    } else {
      setFieldValue("bank", {
        ...options.nullBankOption(),
        routingNumber: bank,
      });
      setFieldValue("bankName", "");
      setFieldValue("nickname", values.nickname || "");
      setFieldValue("accountType", values.accountType || "checking");
      setFieldValue("accountNumber", values.accountNumber || "");
      setBankInputValue(
        typeof bank === "string" ? bank : bank?.routingNumber || "",
      );
    }

    const updatedErrors = { ...errors };
    delete updatedErrors.bank;
    delete updatedErrors.nickname;
    delete updatedErrors.accountType;
    delete updatedErrors.accountNumber;
    delete updatedErrors.routingNumber;
    setErrors(updatedErrors);

    setFieldTouched("bank", false, false);
    setFieldTouched("bankName", false, false);
    setFieldTouched("nickname", false, false);
    setFieldTouched("accountType", false, false);
    setFieldTouched("accountNumber", false, false);
    setFieldTouched("routingNumber", false, false);

    // Bank selection invalidates verification
    if (requiresVerification) setVerifiedTs(null);
  };

  const handleAccountTypeChange = (e) => {
    setFieldValue("accountType", e.target.value);
  };

  if (!initialized || !banksLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Stack spacing="30px" height="100%">
      <FormContainer>
        {/* Complete Setup Later banner */}
        {showCompleteSetupLater && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#f8f9fc",
              border: "1px solid #ACBEE8",
              borderRadius: "8px",
            }}
          >
            <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
              Complete Setup Later
            </Typography>
            <Typography sx={{ fontSize: "14px" }}>
              Billing setup can be completed now or later by you or the employer. You can continue setup by navigating to the Administrator Dashboard to add employees and their spouses and dependents.
            </Typography>
            <Typography
              component="a"
              href={`/employer/${employer.id}`}
              sx={{
                display: "block",
                marginTop: "8px",
                color: "#6C63FF",
                fontSize: "14px",
                cursor: "pointer",
                textDecoration: "underline",
                "&:hover": { color: "#5650d8" },
              }}
            >
              Go to Administrator Dashboard
            </Typography>
          </Box>
        )}

        <Typography variant="h2" sx={employerFlowStyles.h2}>
          Billing
        </Typography>
        <Typography sx={{ marginBottom: "8px" }}>
          How would you like to pay for Salusion?
        </Typography>

        <Stack spacing={2} sx={{ marginTop: "20px" }}>
          {/* Billing Method */}
          <Stack>
            <Typography sx={{ marginBottom: "8px" }}>Billing Method</Typography>
            <Field name="billingMethod">
              {({ field }) => (
                <select
                  value={values.billingMethod?.value || ""}
                  onChange={(e) => {
                    handleBillingMethodChange(e);
                    field.onChange(e);
                  }}
                  disabled={displayOnly}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: `1px solid ${errors.billingMethod && touched.billingMethod ? "#dc2626" : "#ACBEE8"}`,
                    borderRadius: "8px",
                    fontSize: "14px",
                    backgroundColor: displayOnly ? "#f3f4f6" : "white",
                    cursor: displayOnly ? "not-allowed" : "pointer",
                  }}
                >
                  <option value="">Select billing method</option>
                  {options
                    .billingMethods(reimbursementAccount)
                    ?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.display || option.value}
                      </option>
                    ))}
                </select>
              )}
            </Field>
            {errors.billingMethod && touched.billingMethod && (
              <div className="error-message-fields">{errors.billingMethod}</div>
            )}
          </Stack>

          {/* Bank Account Fields */}
          {showBank && (
            <Fragment>
              {/* Routing Number + Bank Name */}
              <Stack direction="row" spacing={2}>
                <Stack flex={1}>
                  <Typography sx={{ marginBottom: "8px" }}>
                    Routing Number
                  </Typography>
                  <Field name="bank">
                    {({ field }) => (
                      <MuiAutocomplete
                        value={values.bank || null}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            handleBankSelect(event, newValue);
                            field.onChange({
                              target: { name: field.name, value: newValue },
                            });
                          } else {
                            clearBankAccountFields();
                            field.onChange({
                              target: { name: field.name, value: null },
                            });
                          }
                        }}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === "reset") return;
                          handleBankInput(newInputValue);
                        }}
                        inputValue={bankInputValue}
                        getOptionLabel={(option) =>
                          option?.displayName || option?.routingNumber || ""
                        }
                        options={banksState}
                        freeSolo
                        disabled={isBankFieldsDisabled}
                        renderInput={(params) => (
                          <MuiTextField
                            {...params}
                            error={!!(errors.bank && touched.bank)}
                            helperText={
                              errors.bank && touched.bank ? errors.bank : ""
                            }
                            sx={{
                              width: "100%",
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontSize: "14px",
                                backgroundColor: isBankFieldsDisabled
                                  ? "#f3f4f6"
                                  : "white",
                                cursor: isBankFieldsDisabled
                                  ? "not-allowed"
                                  : "text",
                                "& fieldset": {
                                  borderColor:
                                    errors.bank && touched.bank
                                      ? "#d32f2f"
                                      : "#ACBEE8",
                                  borderWidth: "1px",
                                },
                                "&:hover fieldset": {
                                  borderColor:
                                    errors.bank && touched.bank
                                      ? "#d32f2f"
                                      : "#ACBEE8",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor:
                                    errors.bank && touched.bank
                                      ? "#d32f2f"
                                      : "#ACBEE8",
                                },
                                "&.Mui-disabled": {
                                  backgroundColor: "#f3f4f6",
                                  cursor: "not-allowed",
                                  "& fieldset": { borderColor: "#ACBEE8" },
                                },
                              },
                              "& .MuiOutlinedInput-input": { padding: "14px" },
                            }}
                          />
                        )}
                        sx={{ width: "100%" }}
                        slotProps={{
                          popper: {
                            sx: {
                              marginLeft: "0 !important",
                              "& .MuiPaper-root": {
                                marginLeft: "0 !important",
                                fontSize: "14px",
                                "& .MuiAutocomplete-listbox": {
                                  fontSize: "14px",
                                  "& .MuiAutocomplete-option": {
                                    fontSize: "14px",
                                  },
                                },
                              },
                            },
                          },
                        }}
                      />
                    )}
                  </Field>
                </Stack>

                <Stack flex={1}>
                  <Typography sx={{ marginBottom: "8px" }}>
                    Bank Name
                  </Typography>
                  <Field name="bankName">
                    {({ field }) => (
                      <MuiTextField
                        {...field}
                        fullWidth
                        disabled={true}
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            fontSize: "14px",
                            backgroundColor: "#f3f4f6",
                            cursor: "not-allowed",
                            "& fieldset": {
                              borderColor: "#ACBEE8",
                              borderWidth: "1px",
                            },
                          },
                          "& .MuiOutlinedInput-input": { padding: "14px" },
                        }}
                      />
                    )}
                  </Field>
                </Stack>
              </Stack>

              {/* Additional fields for BILLING method only */}
              {billingMethod === "BILLING" && (
                <Fragment>
                  <Stack direction="row" spacing={2}>
                    {/* Nickname */}
                    <Stack flex={1}>
                      <Typography sx={{ marginBottom: "8px" }}>
                        Nickname
                      </Typography>
                      <Field name="nickname">
                        {({ field }) => (
                          <MuiTextField
                            {...field}
                            fullWidth
                            disabled={displayOnly}
                            error={!!(errors.nickname && touched.nickname)}
                            helperText={
                              errors.nickname && touched.nickname
                                ? errors.nickname
                                : ""
                            }
                            sx={{
                              width: "100%",
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontSize: "14px",
                                backgroundColor: displayOnly
                                  ? "#f3f4f6"
                                  : "white",
                                cursor: displayOnly ? "not-allowed" : "text",
                                "& fieldset": {
                                  borderColor:
                                    errors.nickname && touched.nickname
                                      ? "#d32f2f"
                                      : "#ACBEE8",
                                  borderWidth: "1px",
                                },
                                "&:hover fieldset": {
                                  borderColor:
                                    errors.nickname && touched.nickname
                                      ? "#d32f2f"
                                      : "#ACBEE8",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor:
                                    errors.nickname && touched.nickname
                                      ? "#d32f2f"
                                      : "#ACBEE8",
                                },
                                "&.Mui-disabled": {
                                  backgroundColor: "#f3f4f6",
                                  cursor: "not-allowed",
                                  "& fieldset": { borderColor: "#ACBEE8" },
                                },
                              },
                              "& .MuiOutlinedInput-input": { padding: "14px" },
                            }}
                          />
                        )}
                      </Field>
                    </Stack>

                    {/* Account Type */}
                    <Stack flex={1}>
                      <Typography sx={{ marginBottom: "8px" }}>
                        Account Type
                      </Typography>
                      <Field name="accountType">
                        {({ field }) => (
                          <select
                            value={values.accountType || "checking"}
                            onChange={(e) => {
                              handleAccountTypeChange(e);
                              field.onChange(e);
                            }}
                            disabled={displayOnly}
                            style={{
                              width: "100%",
                              padding: "12px",
                              border: "1px solid #ACBEE8",
                              borderRadius: "8px",
                              fontSize: "14px",
                              backgroundColor: displayOnly
                                ? "#f3f4f6"
                                : "white",
                              cursor: displayOnly ? "not-allowed" : "pointer",
                            }}
                          >
                            <option value="checking">Checking</option>
                            <option value="savings">Savings</option>
                          </select>
                        )}
                      </Field>
                      {errors.accountType && touched.accountType && (
                        <div className="error-message-fields">
                          {errors.accountType}
                        </div>
                      )}
                    </Stack>
                  </Stack>

                  {/* Account Number */}
                  <Stack>
                    <Typography sx={{ marginBottom: "8px" }}>
                      Account Number
                    </Typography>
                    <Field name="accountNumber">
                      {({ field }) => (
                        <MuiTextField
                          {...field}
                          type="text"
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, "");
                            field.onChange({
                              target: { name: field.name, value: val },
                            });
                            // Account number change invalidates verification
                            setVerifiedTs(null);
                          }}
                          fullWidth
                          disabled={displayOnly}
                          error={
                            !!(errors.accountNumber && touched.accountNumber)
                          }
                          helperText={
                            errors.accountNumber && touched.accountNumber
                              ? errors.accountNumber
                              : ""
                          }
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              fontSize: "14px",
                              backgroundColor: displayOnly
                                ? "#f3f4f6"
                                : "white",
                              cursor: displayOnly ? "not-allowed" : "text",
                              "& fieldset": {
                                borderColor:
                                  errors.accountNumber && touched.accountNumber
                                    ? "#d32f2f"
                                    : "#ACBEE8",
                                borderWidth: "1px",
                              },
                              "&:hover fieldset": {
                                borderColor:
                                  errors.accountNumber && touched.accountNumber
                                    ? "#d32f2f"
                                    : "#ACBEE8",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor:
                                  errors.accountNumber && touched.accountNumber
                                    ? "#d32f2f"
                                    : "#ACBEE8",
                              },
                              "&.Mui-disabled": {
                                backgroundColor: "#f3f4f6",
                                cursor: "not-allowed",
                                "& fieldset": { borderColor: "#ACBEE8" },
                              },
                            },
                            "& .MuiOutlinedInput-input": { padding: "14px" },
                          }}
                        />
                      )}
                    </Field>
                  </Stack>

                  {/* Verified confirmation message */}
                  {verifiedTs && (
                    <Alert
                      icon={<CheckCircleIcon fontSize="inherit" />}
                      severity="success"
                      sx={{ mt: 2 }}
                    >
                      This bank account has been verified. Please continue.
                    </Alert>
                  )}
                </Fragment>
              )}
            </Fragment>
          )}

          {/* Stripe Payment Method */}
          {showStripe && (
            <Fragment>
              <Stack>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleStripe}
                  disabled={displayOnly}
                  sx={{
                    ...styles.stripeButton,
                    borderRadius: "8px",
                    textTransform: "none",
                    padding: "12px 24px",
                  }}
                >
                  {stripeStatus === "ACTIVE"
                    ? "Update Credit Card"
                    : "Add Credit Card"}
                </Button>
              </Stack>
            </Fragment>
          )}

          {/* Current Card on File */}
          {showStripe && stripeStatus === "ACTIVE" && (
            <Fragment>
              <Typography
                variant="body2"
                sx={{ marginTop: "16px", marginBottom: "8px" }}
              >
                Current Card on File
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Stack>
                    <Typography sx={{ marginBottom: "8px" }}>
                      Card Number
                    </Typography>
                    <Field name="stripeNumber">
                      {({ field }) => (
                        <MuiTextField
                          {...field}
                          fullWidth
                          disabled={true}
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              fontSize: "14px",
                              backgroundColor: "#f3f4f6",
                              cursor: "not-allowed",
                              "& fieldset": {
                                borderColor: "#ACBEE8",
                                borderWidth: "1px",
                              },
                            },
                            "& .MuiOutlinedInput-input": { padding: "14px" },
                          }}
                        />
                      )}
                    </Field>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack>
                    <Typography sx={{ marginBottom: "8px" }}>
                      Expiration Date
                    </Typography>
                    <Field name="stripeExpiration">
                      {({ field }) => (
                        <MuiTextField
                          {...field}
                          fullWidth
                          disabled={true}
                          sx={{
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              fontSize: "14px",
                              backgroundColor: "#f3f4f6",
                              cursor: "not-allowed",
                              "& fieldset": {
                                borderColor: "#ACBEE8",
                                borderWidth: "1px",
                              },
                            },
                            "& .MuiOutlinedInput-input": { padding: "14px" },
                          }}
                        />
                      )}
                    </Field>
                  </Stack>
                </Grid>
              </Grid>
            </Fragment>
          )}
        </Stack>
      </FormContainer>

      {/* ACH authorization reminder — only when paying via ACH from Billing Account */}
      {billingMethod === "BILLING" && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: "#f8f9fc",
            border: "1px solid #ACBEE8",
            borderRadius: "8px",
          }}
        >
          <Typography sx={{ fontWeight: 600, marginBottom: "8px" }}>
            Make sure your bank will allow ACH withdrawals from Salusion
          </Typography>
          <Typography sx={{ fontSize: "14px", marginBottom: "8px" }}>
            If your bank uses Positive Pay, ACH filters, or account
            verification, please approve Salusion’s ACH Company ID to avoid
            rejected ACH transactions and related fees.
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            Reimbursement funding: ACH Company ID <strong>2844032944</strong>
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            Billing: ACH Company ID <strong>1844032944</strong>
          </Typography>
        </Box>
      )}

      <Stack
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack spacing={2} sx={{ flex: 1 }}>
          <NavButtons
            mode={mode}
            enableSave={true}
            exitUrl={exitUrl}
            step={step}
            hideNext={hideNext}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}